import React, { useEffect, useMemo, useState } from 'react'
import {
  ChartFrame,
  DataTable,
  NavList,
  Search,
  SearchWithFilter,
  Select,
  SingleVerticalBar,
  Tabs,
  Tooltip,
  type DataTableColumn,
  type DataTableSortDirection,
  type FilterSelection,
  type TabItem,
} from '@enamelai/design-system'
import { InsurancePlan } from '@dataTypes/insurance.types'
import { v4 as uuid } from 'uuid'
import {
  getCoverageLevelText,
  getDataSourceText,
  getNetworkTypeText,
  getVisitPeriodUnitText,
} from '@utils/insurance.util'
import InsuranceEmptyCard from '@components/pages/Insurance/InsuranceEmptyCard/InsuranceEmptyCard.component'
import InsuranceSkeleton from '@pages/Insurance/InsuranceSkeleton.page'

interface PlanCoverageProps {
  insurancePlan: InsurancePlan
  carriers: { value: string; label: string }[]
  onChangeCarrier: (index: number) => void
  uuId: React.MutableRefObject<string>
  isPending?: boolean
}

type CoverageTab = 'benefits' | 'deductibles' | 'maximums'
type CoverageRecord = Record<string, any>
type CoverageRow = { id: string }

const COVERAGE_TABS: TabItem[] = [
  { id: 'benefits', label: 'Benefits' },
  { id: 'deductibles', label: 'Deductibles' },
  { id: 'maximums', label: 'Maximums' },
]

const EMPTY_COPY: Record<CoverageTab, { title: string; description: string }> =
  {
    benefits: {
      title: 'No benefits found',
      description:
        "You don't have any benefits on record for this service type.",
    },
    deductibles: {
      title: 'No deductibles found',
      description:
        "You don't have any deductibles on record for this service type.",
    },
    maximums: {
      title: 'No maximums found',
      description:
        "You don't have any maximums on record for this service type.",
    },
  }

const DEFAULT_PAGE_SIZE = 10
const CHART_PLOT_HEIGHT = 230
const CHART_BAR_WIDTH = 200

type ChartBar = {
  label: string
  color: string
  /** Bar fill, 0-100 of the plot height. */
  fill: number
  /** Text shown inside the bar and in its tooltip. */
  display: string
}

const CHART_USED_COLOR = 'var(--ds-blue-600)'
const CHART_REMAINING_COLOR = 'var(--ds-blue-300)'

const getServiceCategoryValue = (item: unknown): string => {
  if (typeof item === 'string') return item
  if (item && typeof item === 'object') {
    return Object.values(item as Record<string, unknown>).join('')
  }
  return ''
}

const formatServiceCategoryLabel = (category: string): string =>
  category.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())

const filterData = (selectedTab: CoverageTab): Record<string, string[]> => {
  const visitPeriodUnits = [
    'Year',
    'Month',
    'Day',
    'Lifetime',
    'Visit',
    'Coverage Year',
  ]

  return {
    network_type: ['In-network', 'Out-of-network'],
    ...(selectedTab !== 'benefits'
      ? { coverage_level: ['Family', 'Individual', 'Unknown'] }
      : {}),
    ...(selectedTab === 'benefits'
      ? { visit_period_unit: visitPeriodUnits }
      : { period_unit: visitPeriodUnits }),
    source: ['EDI', 'Payer Portal'],
  }
}

const filterLabels = (selectedTab: CoverageTab): Record<string, string> => ({
  network_type: 'Network type',
  ...(selectedTab !== 'benefits' ? { coverage_level: 'Coverage level' } : {}),
  ...(selectedTab === 'benefits'
    ? { visit_period_unit: 'Visit period unit' }
    : { period_unit: 'Period unit' }),
  source: 'Source',
})

const matchesQuery = (
  item: CoverageRecord,
  query: string,
  fields: string[],
): boolean =>
  fields.some((field) => {
    const value = item[field]
    const valueAsString = value != null ? value.toString().toLowerCase() : ''
    return valueAsString.includes(query)
  })

const formatMoney = (value: unknown): string => `$${value}`

const compareValues = (left: unknown, right: unknown): number => {
  const leftNumber = Number(left)
  const rightNumber = Number(right)
  if (
    left !== '' &&
    right !== '' &&
    !Number.isNaN(leftNumber) &&
    !Number.isNaN(rightNumber)
  ) {
    return leftNumber - rightNumber
  }
  return String(left ?? '').localeCompare(String(right ?? ''))
}

const PlanCoverage: React.FC<PlanCoverageProps> = ({
  insurancePlan,
  carriers,
  onChangeCarrier,
  uuId,
  isPending = false,
}) => {
  const [serviceCategory, setServiceCategory] = useState<string | undefined>(
    undefined,
  )
  const [tab, setTab] = useState<CoverageTab>('benefits')
  const [procedureInput, setProcedureInput] = useState('')
  const [procedureFilter, setProcedureFilter] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchFilters, setSearchFilters] = useState<FilterSelection>({})
  const [sortColumnId, setSortColumnId] = useState<string | null>(null)
  const [sortDirection, setSortDirection] =
    useState<DataTableSortDirection>('asc')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [selectedRowIndex, setSelectedRowIndex] = useState(0)

  const planId = insurancePlan?.insurance_plan_id

  // Default to the first service category, and recover when the carrier changes.
  useEffect(() => {
    const services = insurancePlan?.services ?? []
    if (services.length === 0) return
    if (!services.some((service) => service.category === serviceCategory)) {
      setServiceCategory(services[0].category)
    }
  }, [insurancePlan?.services, serviceCategory, planId])

  const selectServiceCategory = (category: string): void => {
    if (!category || category === serviceCategory) return
    uuId.current = uuid()
    setServiceCategory(category)
  }

  const handleSearchProcedure = (rawValue: string): void => {
    const query = rawValue.toLowerCase().trim()
    if (query === '') {
      setProcedureFilter(null)
      return
    }
    const data =
      insurancePlan?.services?.flatMap((service) => service.benefits) || []
    const procedures = data.filter((item) =>
      matchesQuery(item, query, ['procedure_code', 'procedure_description']),
    )
    const matchedCategory = procedures[0]?.category
    if (matchedCategory !== undefined) {
      setTab('benefits')
      selectServiceCategory(matchedCategory)
      setProcedureFilter(query)
    }
  }

  const clearProcedureSearch = (): void => {
    setProcedureInput('')
    setProcedureFilter(null)
  }

  const clearAllFilters = (): void => {
    setSearchFilters({})
    setSearchQuery('')
  }

  const sortedData = useMemo(() => {
    const service = insurancePlan?.services?.find(
      (x) => x.category === serviceCategory,
    )
    let data: CoverageRecord[] = service?.[tab] || []

    if (procedureFilter !== null && tab === 'benefits') {
      data = data.filter((item) =>
        matchesQuery(item, procedureFilter, [
          'procedure_code',
          'procedure_description',
        ]),
      )
    }

    if (searchQuery !== '') {
      const query = searchQuery.toLowerCase()
      data = data.filter((item) =>
        matchesQuery(item, query, [
          'procedure_code',
          'procedure_description',
          'network',
        ]),
      )
    }

    const selected = (key: string): string[] => searchFilters[key] ?? []

    if (selected('network_type').length > 0) {
      data = data.filter((x) =>
        selected('network_type').includes(
          getNetworkTypeText(x.network_type) || '',
        ),
      )
    }

    if (selected('coverage_level').length > 0 && tab !== 'benefits') {
      data = data.filter((x) =>
        selected('coverage_level').includes(
          getCoverageLevelText(x.coverage_level) || '',
        ),
      )
    }

    if (selected('visit_period_unit').length > 0) {
      data = data.filter((x) =>
        selected('visit_period_unit').includes(
          getVisitPeriodUnitText(x.visit_period_unit) || '',
        ),
      )
    }

    if (selected('period_unit').length > 0 && tab !== 'benefits') {
      data = data.filter((x) =>
        selected('period_unit').includes(
          getVisitPeriodUnitText(x.period_unit) || '',
        ),
      )
    }

    if (selected('source').length > 0) {
      data = data.filter((x) =>
        selected('source').includes(getDataSourceText(x.source) || ''),
      )
    }

    if (sortColumnId) {
      const direction = sortDirection === 'asc' ? 1 : -1
      data = [...data].sort(
        (a, b) => compareValues(a[sortColumnId], b[sortColumnId]) * direction,
      )
    }

    return data
  }, [
    insurancePlan?.services,
    serviceCategory,
    tab,
    procedureFilter,
    searchQuery,
    searchFilters,
    sortColumnId,
    sortDirection,
  ])
  const resetKey = [
    tab,
    serviceCategory,
    planId,
    searchQuery,
    JSON.stringify(searchFilters),
    procedureFilter,
  ].join('|')

  useEffect(() => {
    setPage(1)
    setSelectedRowIndex(0)
  }, [resetKey])

  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize))
  const currentPage = Math.min(page, totalPages)
  const pageOffset = (currentPage - 1) * pageSize

  const rows = useMemo<CoverageRow[]>(
    () =>
      sortedData
        .slice(pageOffset, pageOffset + pageSize)
        .map((_, index) => ({ id: `coverage-row-${pageOffset + index}` })),
    [sortedData, pageOffset, pageSize],
  )

  const recordAt = (id: string): CoverageRecord | undefined =>
    sortedData[Number(id.replace('coverage-row-', ''))]

  const selectedRecord = sortedData[selectedRowIndex]
  const selectedRowIds = selectedRecord
    ? [`coverage-row-${selectedRowIndex}`]
    : []

  const text = (value: unknown): React.ReactNode =>
    value ? (value as React.ReactNode) : '-'

  const columns = useMemo<DataTableColumn<CoverageRow>[]>(() => {
    const marker = (id: string, node: React.ReactNode): React.ReactNode => (
      <span data-coverage-row-id={id}>{node}</span>
    )

    if (tab === 'benefits') {
      return [
        {
          id: 'procedure_code',
          label: 'Code',
          sortable: true,
          width: 90,
          renderCell: ({ id }) => marker(id, recordAt(id)?.procedure_code),
        },
        {
          id: 'procedure_description',
          label: 'Description',
          width: 260,
          sortable: true,
          renderCell: ({ id }) => {
            const description = recordAt(id)?.procedure_description
            return (
              <Tooltip content={description} placement="top" portal>
                <span className="coverage-description">{description}</span>
              </Tooltip>
            )
          },
        },
        {
          id: 'network_type',
          label: 'Network',
          width: 130,
          sortable: true,
          renderCell: ({ id }) =>
            getNetworkTypeText(recordAt(id)?.network_type),
        },
        {
          id: 'co_insurance',
          label: 'Coinsurance',
          width: 120,
          sortable: true,
          renderCell: ({ id }) => `${recordAt(id)?.co_insurance}%`,
        },
        {
          id: 'co_pay',
          label: 'Copay',
          width: 90,
          sortable: true,
          renderCell: ({ id }) => {
            const coPay = recordAt(id)?.co_pay
            return coPay ? formatMoney(coPay) : '-'
          },
        },
        {
          id: 'age_maximum',
          label: 'Age max.',
          width: 90,
          sortable: true,
          renderCell: ({ id }) => text(recordAt(id)?.age_maximum),
        },
        {
          id: 'visit_limit',
          label: 'Visit limit',
          width: 100,
          sortable: true,
          renderCell: ({ id }) => text(recordAt(id)?.visit_limit),
        },
        {
          id: 'visit_period',
          label: 'Visit period',
          width: 130,
          sortable: true,
          renderCell: ({ id }) => {
            const record = recordAt(id)
            if (!record?.visit_period) return '-'
            return `${record.visit_period} ${getVisitPeriodUnitText(
              record.visit_period_unit,
            )}${Number(record.visit_period) > 1 ? 's' : ''}`
          },
        },
      ]
    }

    return [
      {
        id: 'network',
        label: 'Network',
        width: 220,
        sortable: true,
        renderCell: ({ id }) => marker(id, recordAt(id)?.network),
      },
      {
        id: 'network_type',
        label: 'Type',
        width: 130,
        sortable: true,
        renderCell: ({ id }) => getNetworkTypeText(recordAt(id)?.network_type),
      },
      {
        id: 'coverage_level',
        label: 'Coverage level',
        width: 150,
        sortable: true,
        renderCell: ({ id }) =>
          getCoverageLevelText(recordAt(id)?.coverage_level),
      },
      {
        id: 'amount',
        label: 'Amount',
        width: 110,
        sortable: true,
        renderCell: ({ id }) => formatMoney(recordAt(id)?.amount),
      },
      {
        id: 'remaining',
        label: 'Remaining',
        width: 120,
        sortable: true,
        renderCell: ({ id }) => formatMoney(recordAt(id)?.remaining),
      },
      {
        id: 'period_unit',
        label: 'Period unit',
        width: 130,
        sortable: true,
        renderCell: ({ id }) =>
          getVisitPeriodUnitText(recordAt(id)?.period_unit),
      },
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, sortedData])

  const handleSort = (columnId: string): void => {
    if (columnId === sortColumnId) {
      setSortDirection((direction) => (direction === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortColumnId(columnId)
      setSortDirection('asc')
    }
    setPage(1)
    setSelectedRowIndex(0)
  }

  const handleTableClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    const target = event.target as HTMLElement
    if (target.closest('button, a, input, textarea, select, [role="menu"]')) {
      return
    }
    const rowId = target
      .closest('tr.ds-data-table__row')
      ?.querySelector<HTMLElement>('[data-coverage-row-id]')
      ?.getAttribute('data-coverage-row-id')
    if (!rowId) return
    const index = Number(rowId.replace('coverage-row-', ''))
    if (!Number.isNaN(index)) setSelectedRowIndex(index)
  }

  // Two separate columns (not stacked), as in the original chart.
  const chart = useMemo((): { ticks: string[]; bars: ChartBar[] } => {
    const percentTicks = ['100%', '75%', '50%', '25%', '0%']
    if (!selectedRecord) return { ticks: percentTicks, bars: [] }

    if (tab === 'benefits') {
      const coInsurance = Math.min(
        100,
        Math.max(0, Number(selectedRecord.co_insurance) || 0),
      )
      return {
        ticks: percentTicks,
        bars: [
          {
            label: 'Insurance coverage',
            color: CHART_USED_COLOR,
            fill: coInsurance,
            display: `${coInsurance}%`,
          },
          {
            label: 'Patient responsibility',
            color: CHART_REMAINING_COLOR,
            fill: 100 - coInsurance,
            display: `${100 - coInsurance}%`,
          },
        ],
      }
    }

    const amount = Number(selectedRecord.amount) || 0
    const remaining = Math.min(Number(selectedRecord.remaining) || 0, amount)
    return {
      ticks: [1, 0.75, 0.5, 0.25, 0].map((ratio) =>
        formatMoney(Math.round(amount * ratio * 100) / 100),
      ),
      bars: [
        {
          label: 'Amount',
          color: CHART_USED_COLOR,
          fill: amount > 0 ? 100 : 0,
          display: formatMoney(amount),
        },
        {
          label: 'Remaining',
          color: CHART_REMAINING_COLOR,
          fill: amount > 0 ? (remaining / amount) * 100 : 0,
          display: formatMoney(remaining),
        },
      ],
    }
  }, [selectedRecord, tab])

  const serviceCategories = (
    (insurancePlan?.categories?.[tab] as unknown[] | undefined) ?? []
  ).map(getServiceCategoryValue)

  if (isPending) {
    return <InsuranceSkeleton variant="coverage" />
  }

  const emptyCopy = EMPTY_COPY[tab]

  return (
    <div className="section-insurance-coverage">
      <div className="coverage-layout">
        <aside className="coverage-side-panel-wrapper">
          <div className="coverage-side-panel">
            <div className="coverage-side-panel-heading">
              <span className="coverage-side-panel-title">
                Service type coverage
              </span>
            </div>
            <div className="coverage-side-panel-controls">
              <div
                className="coverage-procedure-search"
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleSearchProcedure(procedureInput)
                  }
                }}
              >
                <Search
                  placeholder="Search procedures"
                  ariaLabel="Search procedures"
                  value={procedureInput}
                  onChange={setProcedureInput}
                  onClear={clearProcedureSearch}
                />
              </div>
              <Select
                label="Carrier name"
                options={carriers}
                value={planId}
                onChange={(value) => {
                  const index = carriers.findIndex(
                    (carrier) => carrier.value === value,
                  )
                  if (index !== -1) onChangeCarrier(index)
                }}
              />
            </div>
            <div className="coverage-sidebar-group">
              <NavList
                items={serviceCategories.map((category) => ({
                  id: category,
                  label: formatServiceCategoryLabel(category),
                }))}
                value={serviceCategory}
                onChange={selectServiceCategory}
                showChevron={false}
              />
            </div>
          </div>
        </aside>
        <div className="deductions-limitations-col">
          <div className="coverage-chart">
            <ChartFrame
              plotHeight={CHART_PLOT_HEIGHT}
              xLabels={
                chart.bars.length > 0
                  ? chart.bars.map((bar) => bar.label)
                  : ['No selection']
              }
              yTicks={chart.ticks}
              showXAxisLabel={false}
              showYAxisLabel={false}
            >
              {chart.bars.length > 0 && (
                <div className="coverage-chart__bars">
                  {chart.bars.map((bar, index) => (
                    <div key={bar.label} className="coverage-chart__bar">
                      <div
                        className="coverage-chart__bar-inner"
                        style={{ height: CHART_PLOT_HEIGHT }}
                      >
                        <SingleVerticalBar
                          title={bar.label}
                          hideTitle
                          width={CHART_BAR_WIDTH}
                          height={CHART_PLOT_HEIGHT}
                          color={bar.color}
                          value={bar.fill}
                          formatValue={() => bar.display}
                          tooltipLabel={bar.label}
                        />
                        {bar.fill >= 10 && (
                          <span
                            className={`coverage-chart__bar-value${
                              index === 0
                                ? ' coverage-chart__bar-value--on-dark'
                                : ''
                            }`}
                            style={{ bottom: `${bar.fill / 2}%` }}
                          >
                            {bar.display}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ChartFrame>
          </div>
          <div className="coverage-table-header">
            <Tabs
              items={COVERAGE_TABS}
              value={tab}
              onChange={(tabId) => setTab(tabId as CoverageTab)}
              className="coverage-tabs"
            />
            <SearchWithFilter
              className="coverage-search-filter"
              placeholder="Search..."
              ariaLabel="Search coverage"
              value={searchQuery}
              onChange={setSearchQuery}
              filters={filterData(tab)}
              filterLabels={filterLabels(tab)}
              selectedFilters={searchFilters}
              onFilterChange={setSearchFilters}
              showResetButton
              onReset={clearAllFilters}
            />
          </div>
          <div
            className="insurance-table insurance-table--selectable coverage-table"
            onClick={handleTableClick}
          >
            {sortedData.length === 0 ? (
              <InsuranceEmptyCard
                iconClass="mgc mgc_shield_fill"
                emptyTitle={emptyCopy.title}
                description={emptyCopy.description}
                outlined={false}
              />
            ) : (
              <DataTable<CoverageRow>
                columns={columns}
                rows={rows}
                showActions={false}
                selectedRowIds={selectedRowIds}
                sortColumnId={sortColumnId ?? undefined}
                sortDirection={sortColumnId ? sortDirection : undefined}
                onSort={handleSort}
                pagination={{
                  currentPage,
                  totalPages,
                  pageSize,
                  onPageChange: (nextPage) => {
                    setPage(nextPage)
                    setSelectedRowIndex((nextPage - 1) * pageSize)
                  },
                  onPageSizeChange: (size) => {
                    setPageSize(size)
                    setPage(1)
                    setSelectedRowIndex(0)
                  },
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default PlanCoverage
